import { Box, Divider, Typography } from '@mui/material';
import { modules } from '@terraframing-suite/rx-config';
import { useEffect, useMemo } from 'react';
import { useContext } from './context.jsx';
import { usePermissions } from '../../hooks/use-permissions.js';
import { Radio } from './form-controls.jsx';

export function ModuleSelector() {
  const permissions = usePermissions();
  const { selectedModules, toggleModule, focusedModule } = useContext();

  useEffect(
    () => focusedModule && toggleModule(focusedModule),
    [focusedModule, toggleModule],
  );

  const selectedDependencies = useMemo(
    () =>
      modules.flatMap(({ id, dependencies }) =>
        selectedModules[id] ? dependencies : [],
      ),
    [selectedModules],
  );

  const producers = useMemo(
    () =>
      modules.filter((m) => m.producer && permissions.includes(m.permission)),
    [permissions],
  );

  const consumers = useMemo(
    () =>
      modules.filter((m) => !m.producer && permissions.includes(m.permission)),
    [permissions],
  );

  return focusedModule ? null : (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography>Select the Rx(s) you wish to run.</Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
        }}
      >
        {buildModuleSelectors(producers, { flexDirection: 'column' })}
        {producers.length && consumers.length ? (
          <Divider
            sx={{ display: { xs: 'none', sm: 'block' } }}
            orientation="vertical"
            flexItem
          />
        ) : null}
        {buildModuleSelectors(consumers, { flexWrap: 'wrap' })}
      </Box>
      <Divider />
    </Box>
  );

  function buildModuleSelectors(forModules, sx) {
    return forModules.length ? (
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          ...sx,
        }}
      >
        {forModules.map(buildCheckbox)}
      </Box>
    ) : null;
  }

  function buildCheckbox(module) {
    return (
      <Radio
        radio={false}
        sx={{ width: 'auto' }}
        key={module.id}
        label={module.label}
        value={module.id}
        disabled={selectedDependencies.includes(module.id)}
        checked={Boolean(selectedModules[module.id])}
        onChange={() => toggleModule(module.id)}
      />
    );
  }
}
